import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Gift, Star, Award, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

export const LoyaltySection = () => {
  const { user, api, refreshUser } = useAuth();
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);

  useEffect(() => {
    fetchLoyaltyInfo();
  }, []);

  const fetchLoyaltyInfo = async () => {
    try {
      const res = await api.get('/loyalty');
      setLoyaltyInfo(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (rewardId) => {
    setRedeeming(rewardId);
    try {
      const res = await api.post(`/loyalty/redeem?reward_id=${rewardId}`);
      toast.success(`Redeemed successfully! R${res.data.value} added to your balance`);
      await refreshUser();
      fetchLoyaltyInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Redemption failed');
    } finally {
      setRedeeming(null);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Gold': return 'text-yellow-400';
      case 'Silver': return 'text-gray-300';
      default: return 'text-orange-400';
    }
  };

  const progress = loyaltyInfo 
    ? Math.min((user?.loyalty_points / loyaltyInfo.next_tier_points) * 100, 100) 
    : 0;

  return (
    <div className="space-y-6" data-testid="loyalty-section">
      <h1 className="font-syne text-2xl font-bold text-gray-900">Loyalty Rewards</h1>

      {/* Points Overview */}
      <Card className="card-raoul p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-afriperx-green/10 rounded-full blur-3xl" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-gray-500 text-sm mb-1">Your Points</p>
              <p className="text-4xl font-bold text-gray-900">{user?.loyalty_points?.toLocaleString()}</p>
            </div>
            <div className={`flex items-center gap-2 ${getTierColor(loyaltyInfo?.tier)}`}>
              <Award className="w-8 h-8" />
              <span className="font-syne text-xl font-bold">{loyaltyInfo?.tier}</span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Progress to next tier</span>
              <span className="text-gray-900">{loyaltyInfo?.next_tier_points?.toLocaleString()} points</span>
            </div>
            <Progress value={progress} className="h-2 bg-gray-200" />
          </div>
        </div>
      </Card>

      {/* How to Earn */}
      <Card className="card-raoul p-6">
        <h2 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-afriperx-gold" />
          How to Earn Points
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-afriperx-gold font-bold text-lg">1 point</p>
            <p className="text-gray-500 text-sm">Per R10 spent</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-afriperx-gold font-bold text-lg">2,500 points</p>
            <p className="text-gray-500 text-sm">Welcome bonus</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-afriperx-gold font-bold text-lg">Bonus points</p>
            <p className="text-gray-500 text-sm">For marketplace spending</p>
          </div>
        </div>
      </Card>

      {/* Available Rewards */}
      <Card className="card-raoul p-6">
        <h2 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Gift className="w-5 h-5 text-afriperx-gold" />
          Redeem Your Points
        </h2>
        <div className="space-y-4">
          {loyaltyInfo?.rewards_available?.map((reward) => {
            const canRedeem = user?.loyalty_points >= reward.points_required;
            return (
              <div 
                key={reward.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    canRedeem ? 'bg-afriperx-green/20' : 'bg-gray-100'
                  }`}>
                    <Star className={`w-5 h-5 ${canRedeem ? 'text-afriperx-gold' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">{reward.name}</p>
                    <p className="text-gray-500 text-sm">{reward.points_required.toLocaleString()} points</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleRedeem(reward.id)}
                  disabled={!canRedeem || redeeming === reward.id}
                  className={canRedeem ? 'btn-primary' : 'btn-secondary opacity-50'}
                  data-testid={`redeem-${reward.id}`}
                >
                  {redeeming === reward.id ? 'Redeeming...' : 'Redeem'}
                </Button>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};
